import React from 'react'
import { useTranslation } from 'react-i18next'
import { makeStyles } from 'tss-react/mui'
import type { Theme } from '@mui/material/styles'
import Button from '@mui/material/Button'
import Grid from '@mui/material/Grid'
import Divider from '@mui/material/Divider'
import List from '@mui/material/List'
import ListItem from '@mui/material/ListItem'
import { H2, Text1, Text2 } from 'ui/shorthands/Texts'
import Paper from '@mui/material/Paper'
import information from 'config/information'

const useStyles = makeStyles()((theme: Theme) => ({
  root: {
    margin: 'auto',
    maxWidth: 320,
    paddingBottom: theme.spacing(2),
  },
  header: {
    paddingTop: theme.spacing(2),
    paddingLeft: theme.spacing(2),
    paddingRight: theme.spacing(2),
  },
  h2: { fontSize: 20 },
  subText: {
    marginTop: theme.spacing(1),
    display: 'block',
  },
  listItem: {
    paddingTop: 0,
    paddingBottom: 0,
    '&:not(:first-child)': {
      paddingTop: theme.spacing(1),
    },
  },
  linkText: {
    fontWeight: 'bold',
    textDecoration: 'none',
    color: theme.palette.primary.main,
  },
  divider: {
    marginTop: theme.spacing(2),
    marginBottom: theme.spacing(1),
  },
}))

export type UserSupportPopOverProps = {
  type?: 'med' | 'personal'
}

export const UserSupportPopOver: React.FC<UserSupportPopOverProps> = ({
  type = 'med',
}) => {
  const { t } = useTranslation()
  const { classes } = useStyles()
  const info = information[type]
  return (
    <Paper square>
      <Grid className={classes.root}>
        {type === 'med' && (
          <>
            <Grid className={classes.header}>
              <H2 className={classes.h2}>{t('ご利用マニュアル')}</H2>
              <Text1 className={classes.subText}>
                {t('YaDoc Quickの各種操作手順について詳しくご案内しています。')}
              </Text1>
            </Grid>
            <Grid>
              <List>
                <ListItem>
                  <Button
                    component={'a'}
                    variant="contained"
                    color="primary"
                    href={info.manual}
                    target="_blank"
                    rel="noopener noreferrer"
                  >
                    {t('ご利用マニュアル', { context: 'button' })}
                  </Button>
                </ListItem>
              </List>
            </Grid>
            <Divider className={classes.divider} />
          </>
        )}
        <Grid className={classes.header}>
          <H2 className={classes.h2}>{t('YaDoc Quickサポートセンター')}</H2>
          <Text1 className={classes.subText}>
            {t('不明点・疑問点は、こちらへお問い合わせください。')}
          </Text1>
        </Grid>
        <Grid>
          <List>
            <ListItem className={classes.listItem}>
              <Text2 gray>
                {t(
                  '他社製品のサポートをご案内する場合があります。あらかじめご了承ください。'
                )}
              </Text2>
            </ListItem>
            <ListItem className={classes.listItem}>
              <Text1>
                <a className={classes.linkText} href={`tel:${info.phone}`}>
                  {info.phone}
                </a>
              </Text1>
            </ListItem>
            <ListItem className={classes.listItem}>
              <Text1>
                <a className={classes.linkText} href={`mailto:${info.email}`}>
                  {info.email}
                </a>
              </Text1>
            </ListItem>
            <ListItem className={classes.listItem}>
              <Text1>{t('営業時間: 土日祝日を除く 9時〜19時')}</Text1>
            </ListItem>
          </List>
        </Grid>
        <Divider className={classes.divider} />
        <Grid className={classes.header}>
          <H2 className={classes.h2}>{t('改善リクエスト')}</H2>
          <Text1 className={classes.subText}>
            {t('ご意見、ご要望などはこちらからお送りください。')}
          </Text1>
        </Grid>
        <Grid>
          <List>
            <ListItem>
              <Button
                variant="contained"
                color="primary"
                href={info.requestForm}
                target="_blank"
                rel="noopener noreferrer"
              >
                {t('リクエストを送る')}
              </Button>
            </ListItem>
            <ListItem>
              <Text2>
                {t(
                  '現在、リクエストに必ず返答することをお約束できません。あらかじめご了承ください。'
                )}
              </Text2>
            </ListItem>
          </List>
        </Grid>
      </Grid>
    </Paper>
  )
}
